/*
 * tw-cart-refresh.js
 *
 * Re-fetches cart drawer + header bubble after back/forward navigation (e.g.
 * returning from Shopify checkout) so counts and line items match the server.
 *
 * Also clears any residual GWP line items (properties._gwp === 'true') before
 * refreshing. Gifts are purely visual in the cart — they are added at checkout
 * via the checkout extension, so they must not persist as real line items.
 */
(function () {
  'use strict';

  var refreshPending = false;
  var refreshTimer = null;

  /**
   * @param {string} html
   * @param {string} [selector]
   * @returns {string}
   */
  function getSectionInnerHTML(html, selector) {
    if (!html) return '';
    var el = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector || '.shopify-section');
    return el ? el.innerHTML : '';
  }

  /**
   * @returns {string[]}
   */
  function getSectionIds() {
    var ids = ['cart-drawer', 'cart-icon-bubble'];

    if (document.body.classList.contains('template-cart')) {
      ids.push('main-cart-items', 'main-cart-footer');
    }

    return ids;
  }

  /**
   * @param {string} sectionHtml
   */
  function patchCartDrawer(sectionHtml) {
    if (!sectionHtml) return;

    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var nextDrawer = doc.querySelector('#CartDrawer');
    var currentDrawer = document.getElementById('CartDrawer');

    if (nextDrawer && currentDrawer) {
      currentDrawer.innerHTML = nextDrawer.innerHTML;
    }

    var nextCart = doc.querySelector('cart-drawer');
    var currentCart = document.querySelector('cart-drawer');
    if (nextCart && currentCart) {
      currentCart.classList.toggle(
        'wt-cart--empty',
        nextCart.classList.contains('wt-cart--empty'),
      );
    }
  }

  /**
   * @param {string} sectionHtml
   */
  function patchCartIconBubble(sectionHtml) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !sectionHtml) return;

    var inner = getSectionInnerHTML(sectionHtml, '.shopify-section');
    if (inner) bubble.innerHTML = inner;
  }

  /**
   * @param {string} sectionId
   * @param {string} sectionHtml
   * @param {string} targetId
   * @param {string} [selector]
   */
  function patchSectionContents(sectionId, sectionHtml, targetId, selector) {
    if (!sectionHtml) return;

    var target = document.getElementById(targetId);
    if (!target) return;

    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var source =
      doc.getElementById(targetId) ||
      doc.querySelector('[data-section-id] ' + (selector || '.js-contents'));

    if (!source) {
      var fallback = getSectionInnerHTML(sectionHtml, selector || '.js-contents');
      if (fallback) {
        var slot = target.querySelector(selector || '.js-contents') || target;
        slot.innerHTML = fallback;
      }
      return;
    }

    var slot = target.querySelector(selector || '.js-contents') || target;
    var nextSlot =
      source.querySelector(selector || '.js-contents') || source;
    if (slot && nextSlot) slot.innerHTML = nextSlot.innerHTML;
  }

  /**
   * Removes any cart line items where properties._gwp === 'true'.
   * Gifts are visual-only in the drawer; the checkout extension handles
   * adding them at checkout, so they must not linger as real line items.
   * @returns {Promise}
   */
  function clearGwpItems() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';

    return fetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var updates = {};

        (cart.items || []).forEach(function (item) {
          if (item.properties && item.properties['_gwp'] === 'true') {
            updates[item.key] = 0;
          }
        });

        if (!Object.keys(updates).length) return;

        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates }),
        });
      })
      .catch(function (err) {
        console.warn('[tw-cart-refresh] clearGwpItems failed', err);
      });
  }

  function refreshCartSections() {
    if (refreshPending) return;
    refreshPending = true;

    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    var sectionIds = getSectionIds();
    var url = root + '?sections=' + sectionIds.join(',');

    clearGwpItems().then(function () {
      return fetch(url, { credentials: 'same-origin' });
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart section refresh failed');
        return res.json();
      })
      .then(function (sections) {
        patchCartDrawer(sections['cart-drawer']);
        patchCartIconBubble(sections['cart-icon-bubble']);

        if (sections['main-cart-items']) {
          patchSectionContents(
            'main-cart-items',
            sections['main-cart-items'],
            'main-cart-items',
            '.js-contents',
          );
        }

        if (sections['main-cart-footer']) {
          patchSectionContents(
            'main-cart-footer',
            sections['main-cart-footer'],
            'main-cart-footer',
            '.js-contents',
          );
        }
      })
      .catch(function (err) {
        console.warn('[tw-cart-refresh]', err);
      })
      .finally(function () {
        refreshPending = false;
      });
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCartSections, 200);
  }

  /**
   * @returns {boolean}
   */
  function isBackForwardNavigation() {
    var nav = performance.getEntriesByType('navigation')[0];
    return !!(nav && nav.type === 'back_forward');
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted || isBackForwardNavigation()) {
      scheduleRefresh();
    }
  });
})();
